import React, { useCallback, useEffect, useState } from 'react';
import { mediaQuery, minBreakpoints } from '@noths/polaris-client-styles';
import { css } from 'styled-components';

import { PictureWithLoad } from 'src/components/molecules/PictureWithLoad';
import { CSSVarHeight, HideElement, Placeholder } from 'src/styles/global.styles';
import { Row } from 'src/styles/layout';
import { trackBannerClick } from 'src/tracking/homepageTracking';
import { Image } from 'src/types/Image';
import * as styles from './HomepageHeroBanner.style';

interface LegalInfo {
  color: string;
  display: boolean;
  link: string;
  title: string;
}

export interface HomepageHeroBannerProps {
  backgroundColor: string;
  bannerImage: Image;
  buttonColor: string;
  buttonTextColor: string;
  buttonTitle: string;
  legalInfo: LegalInfo;
  link: string;
  titleImage: string;
  titleImageAlt: string;
  titleImageMobile: string;
}

export interface Props extends HomepageHeroBannerProps {
  testName: string;
}

export const HomepageHeroBanner = ({
  link,
  buttonTitle,
  backgroundColor,
  titleImage,
  titleImageMobile,
  titleImageAlt,
  buttonColor,
  buttonTextColor,
  bannerImage,
  legalInfo,
  testName,
}: Props) => {
  const [loadedImgs, setLoadedImgs] = useState<string[]>([]);
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  const getLoadedImages = useCallback((refSrc) => {
    setLoadedImgs((imgSrc) => [...imgSrc, refSrc]);
  }, []);

  useEffect(() => {
    if (loadedImgs.length === 2) {
      setShowPlaceholder(false);
    }
  }, [loadedImgs.length]);

  return (
    <div css={styles.HeroBannerWrapper} data-testid={`${testName}-HeroBanner`}>
      {showPlaceholder && (
        <div
          data-testid={`${testName}-HeroBannerPlaceholder`}
          css={Placeholder}
          style={
            {
              '--heightDesktop': '300px',
              '--heightMobile': '465px',
            } as CSSVarHeight
          }
        />
      )}
      <a
        css={styles.HeroBannerLink}
        href={link}
        aria-label={buttonTitle}
        onMouseDown={() =>
          trackBannerClick({
            sectionId: 1,
            ctaText: buttonTitle,
            link,
          })
        }
        data-testid={`${testName}-HeroBannerLink`}
      >
        <span css={HideElement}>{buttonTitle}</span>
      </a>
      <Row
        css={`
          flex-direction: column-reverse;
          min-height: 300px;
          ${mediaQuery(
            { min: minBreakpoints.MEDIUM },
            css`
              flex-direction: row;
            `,
          )};
        `}
      >
        {/* @ts-ignore*/}
        <Row.Column size={{ phone: 12, tablet: 5 }}>
          <div
            css={styles.HeroBannerPromo}
            style={{ '--bgcolor': backgroundColor } as styles.CSSVarBanner}
          >
            <div css={styles.HeroBannerPromoContent}>
              <PictureWithLoad
                mobileImage={titleImageMobile}
                desktopImage={titleImage}
                alt={titleImageAlt}
                setLoadedImages={getLoadedImages}
              />
              <button
                type="button"
                css={styles.HeroBannerButton}
                style={
                  {
                    '--buttonBgColor': buttonColor,
                    '--buttonTextColor': buttonTextColor,
                  } as styles.CSSVarButton
                }
                data-testid={`${testName}-HeroBannerButton`}
              >
                {buttonTitle}
                &nbsp;&raquo;
              </button>
              {legalInfo.display && (
                <div
                  data-testid={`${testName}-HeroBannerLegal`}
                  css={styles.TandCs}
                  style={{ '--color': legalInfo.color } as styles.CSSVarLegal}
                >
                  <a href={legalInfo.link}>{legalInfo.title}</a>
                </div>
              )}
            </div>
          </div>
        </Row.Column>
        {/* @ts-ignore*/}
        <Row.Column size={{ phone: 12, tablet: 7 }}>
          <div css={styles.PictureContainer}>
            <PictureWithLoad
              mobileImage={bannerImage.mobile}
              desktopImage={bannerImage.desktop}
              setLoadedImages={getLoadedImages}
              heightDesktop="300px"
              heightMobile="235px"
            />
          </div>
        </Row.Column>
      </Row>
    </div>
  );
};
